import React, { useState, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { useLanguage } from '../context/LanguageContext';
import { displayFyName } from '../utils/fiscalYearDisplay';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  ChevronDown, 
  Check, 
  Lock, 
  AlertTriangle,
  Plus,
  FlaskConical,
  ChevronRight
} from 'lucide-react';

const FiscalYearSelector = ({ onCreateNew }) => {
  const { isDark } = useTheme();
  const { fiscalYears, currentFiscalYear, selectFiscalYear, loading, scenariosById } = useFiscalYear();
  const { language, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);

  // Separate actuals from scenarios, then derive a flat displayItems list
  // including orphan scenarios (GAP P3 fix).
  // - actuals  → sorted DESC by year (most recent first, existing UX preserved)
  // - scenariosByYear (linked) → grouped per actual year for the éprouvette
  //   `🧪 N` expansion mechanism (unchanged)
  // - orphan scenarios (no actual exists for their target year) → sorted ASC
  //   by year, tie-break alphabétique sur scenarios[scenario_id].name
  //   (lookup live ENH-05 2026-06-25 ; fallback fy.name si scenario_id orphelin
  //   — edge non atteignable en prod, cascade DELETE scenario supprime les FY).
  // Final order : actuals DESC, then orphan scenarios ASC concatenated.
  const { displayItems, scenariosByYear } = useMemo(() => {
    const actuals = [];
    const scenarios = {};
    for (const fy of fiscalYears) {
      if (fy.type === 'scenario') {
        const year = fy.year;
        if (!scenarios[year]) scenarios[year] = [];
        scenarios[year].push(fy);
      } else {
        actuals.push(fy);
      }
    }

    // Orphan scenarios : no actual exists for the same target year
    const actualYearsSet = new Set(actuals.map((fy) => fy.year));
    const orphanScenarios = [];
    for (const year in scenarios) {
      if (!actualYearsSet.has(Number(year))) {
        orphanScenarios.push(...scenarios[year]);
      }
    }

    actuals.sort((a, b) => (b.year || 0) - (a.year || 0));
    orphanScenarios.sort((a, b) => {
      const yearDiff = (b.year || 0) - (a.year || 0);
      if (yearDiff !== 0) return yearDiff;
      // ENH-05 lookup live : tri sur scenarios[scenario_id].name, fallback fy.name
      const aName = scenariosById.get(a.scenario_id)?.name ?? a.name ?? '';
      const bName = scenariosById.get(b.scenario_id)?.name ?? b.name ?? '';
      return aName.localeCompare(bName);
    });

    const items = [
      ...actuals.map((fy) => ({ kind: 'actual', fy })),
      ...orphanScenarios.map((fy) => ({ kind: 'orphan_scenario', fy })),
    ];
    return { displayItems: items, scenariosByYear: scenarios };
  }, [fiscalYears, scenariosById]);

  // Check if current selection is a scenario
  const isScenarioSelected = currentFiscalYear?.type === 'scenario';

  if (loading) {
    return (
      <div className={`px-4 py-2 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-100'} animate-pulse`}>
        <div className="h-5 w-32 bg-gray-300 rounded" />
      </div>
    );
  }

  const getStatusIcon = (fy) => {
    if (fy.type === 'scenario') return <FlaskConical className="w-4 h-4 text-violet-500" />;
    switch (fy.status) {
      case 'closed': return <Lock className="w-4 h-4 text-green-500" />;
      case 'rectified': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      default: return <Calendar className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusLabel = (fy) => {
    if (fy.type === 'scenario') return t('fiscalYears.status.scenario');
    switch (fy.status) {
      case 'closed': return t('fiscalYears.status.closed');
      case 'rectified': return t('fiscalYears.status.rectified');
      default: return t('fiscalYears.status.draft');
    }
  };

  const getStatusColor = (fy) => {
    if (fy.type === 'scenario') return 'bg-violet-500/20 text-violet-500';
    switch (fy.status) {
      case 'closed': return 'bg-green-500/20 text-green-500';
      case 'rectified': return 'bg-orange-500/20 text-orange-500';
      default: return 'bg-blue-500/20 text-blue-500';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const locale = language === 'de' ? 'de-DE' : 'fr-FR';
    return new Date(dateStr).toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  };

  const handleSelectFy = (fy) => {
    selectFiscalYear(fy);
    setIsOpen(false);
    setExpandedYear(null);
  };

  const toggleScenarios = (year, e) => {
    e.stopPropagation();
    setExpandedYear(expandedYear === year ? null : year);
  };

  return (
    <div className="relative z-50">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="fiscal-year-selector"
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
          isScenarioSelected
            ? isDark
              ? 'bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 border border-violet-500/30'
              : 'bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200'
            : isDark 
              ? 'bg-slate-700 hover:bg-slate-600 text-white' 
              : 'bg-white hover:bg-gray-50 text-gray-900 shadow-sm border border-gray-200'
        }`}
      >
        {currentFiscalYear ? (
          <>
            {getStatusIcon(currentFiscalYear)}
            <div className="text-left">
              <p className="text-sm font-medium">
                {currentFiscalYear.type === 'scenario'
                  ? (scenariosById.get(currentFiscalYear.scenario_id)?.name ?? t('fiscalYears.status.scenario'))
                  : displayFyName(currentFiscalYear.name, language)
                }
              </p>
              <p className={`text-xs ${isScenarioSelected ? (isDark ? 'text-violet-400' : 'text-violet-500') : isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {currentFiscalYear.type === 'scenario'
                  ? `${t('fiscalYears.status.scenario')} ${currentFiscalYear.year}`
                  : `${formatDate(currentFiscalYear.start_date)} - ${formatDate(currentFiscalYear.end_date)}`
                }
              </p>
            </div>
          </>
        ) : (
          <>
            <Calendar className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
            <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('fiscalYears.noFiscalYear')}
            </span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute top-full left-0 mt-2 w-80 rounded-xl shadow-xl z-[100] overflow-hidden ${
              isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
            }`}
          >
            <div className={`p-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-100'}`}>
              <p className={`text-xs font-medium px-2 py-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {t('fiscalYears.title').toUpperCase()}
              </p>
            </div>
            
            <div className="max-h-72 overflow-y-auto p-2">
              {displayItems.length === 0 ? (
                <p className={`text-sm text-center py-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {t('fiscalYears.noFiscalYear')}
                </p>
              ) : (
                displayItems.map(({ kind, fy }) => {
                  // GAP P3 — orphan scenarios are rendered as standalone rows
                  // (no actual exists on their target year, so no éprouvette
                  // can host them). Smaller font + violet styling to keep them
                  // visually distinct from actuals.
                  if (kind === 'orphan_scenario') {
                    const isSelected = currentFiscalYear?.id === fy.id;
                    return (
                      <button
                        key={fy.id}
                        onClick={() => handleSelectFy(fy)}
                        data-testid={`orphan-scenario-select-${fy.id}`}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
                          isSelected
                            ? isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-50 text-violet-700'
                            : isDark ? 'hover:bg-slate-700/50 text-slate-300' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <FlaskConical className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-[13px] font-medium truncate">
                            {scenariosById.get(fy.scenario_id)?.name ?? fy.name}
                          </p>
                          <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            {t('fiscalYears.status.scenario')} · {fy.year}
                          </p>
                        </div>
                        {isSelected && <Check className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />}
                      </button>
                    );
                  }

                  // kind === 'actual' — existing rendering preserved unchanged
                  const year = fy.year;
                  const scenarios = scenariosByYear[year] || [];
                  const hasScenarios = scenarios.length > 0;
                  const isExpanded = expandedYear === year;
                  const isSelected = currentFiscalYear?.id === fy.id;

                  return (
                    <div key={fy.id}>
                      {/* Actual exercise row */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSelectFy(fy)}
                          data-testid={`fy-select-${fy.id}`}
                          className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                            isSelected
                              ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'
                              : isDark ? 'hover:bg-slate-700 text-white' : 'hover:bg-gray-50 text-gray-900'
                          }`}
                        >
                          {getStatusIcon(fy)}
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium">{displayFyName(fy.name, language)}</p>
                            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              {formatDate(fy.start_date)} - {formatDate(fy.end_date)}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(fy)}`}>
                            {getStatusLabel(fy)}
                          </span>
                          {isSelected && <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                        </button>

                        {/* Scenario badge */}
                        {hasScenarios && (
                          <button
                            onClick={(e) => toggleScenarios(year, e)}
                            data-testid={`scenario-toggle-${year}`}
                            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                              isExpanded
                                ? 'bg-violet-500/20 text-violet-500'
                                : isDark 
                                  ? 'hover:bg-slate-700 text-slate-400 hover:text-violet-400'
                                  : 'hover:bg-violet-50 text-gray-400 hover:text-violet-600'
                            }`}
                            title={`${scenarios.length} scénario(s)`}
                          >
                            <FlaskConical className="w-3.5 h-3.5" />
                            <span>{scenarios.length}</span>
                            <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                        )}
                      </div>

                      {/* Expanded scenarios list */}
                      <AnimatePresence>
                        {isExpanded && hasScenarios && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className={`ml-6 pl-3 border-l-2 ${isDark ? 'border-violet-500/30' : 'border-violet-200'} py-1 space-y-0.5`}>
                              {scenarios.map((s) => {
                                const isSSelected = currentFiscalYear?.id === s.id;
                                return (
                                  <button
                                    key={s.id}
                                    onClick={() => handleSelectFy(s)}
                                    data-testid={`scenario-select-${s.id}`}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                                      isSSelected
                                        ? isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-50 text-violet-700'
                                        : isDark ? 'hover:bg-slate-700/50 text-slate-300' : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                  >
                                    <FlaskConical className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                                    <span className="flex-1 text-left truncate">{scenariosById.get(s.scenario_id)?.name ?? s.name}</span>
                                    {isSSelected && <Check className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />}
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className={`p-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-100'}`}>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onCreateNew && onCreateNew();
                }}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg transition-all ${
                  isDark 
                    ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400' 
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">{t('fiscalYears.newExercise')}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => { setIsOpen(false); setExpandedYear(null); }}
        />
      )}
    </div>
  );
};

export default FiscalYearSelector;
